import { useState } from 'react';
import logger from '@widgets/logger';
import { Controller, useFormContext } from 'react-hook-form';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import ImageUploaderSingle from '@components/ImageUploaderSingle';
import OtpCode from '@components/OtpCode';
import { useTranslation } from '@hooks';
import { couponsService } from '@services';
import { MB } from '@constants/file';

const Settings = ({ isView }) => {
    const [isUploading, setIsUploading] = useState(false);

    const { control, getValues, setValue, watch, clearErrors } = useFormContext();
    const { t } = useTranslation();

    const coverPhotoUrl = watch('coverPhotoUrl');
    const pinEnabled = watch('pinEnabled');
    const coverPhotoEnabled = watch('coverPhotoEnabled');

    const uploadLogo = async e => {
        try {
            setIsUploading(true);
            const data = new FormData();
            data.append('file', e.target.files[0]);
            data.append('fileName', e.target.files[0].name);
            const response = await couponsService.uploadCouponImage(data);
            setValue('coverPhotoUrl', response.publicUrl, { shouldDirty: true, shouldValidate: true });
        } catch (_) {
            logger.error("Couldn't upload cover photo for coupon");
        }
        setIsUploading(false);
        e.target.value = '';
    };

    const onDeleteLogo = () => setValue('coverPhotoUrl', '', { shouldDirty: true, shouldValidate: true });

    return (
        <div>
            <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)', marginBottom: '8px' }}>
                {t('common.settings')}
            </Typography>
            <div>
                <Controller
                    control={control}
                    name="coverPhotoEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('coverPhotoEnabled')}
                            handleChange={event => {
                                setValue('coverPhotoEnabled', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                if (!event.target.checked) {
                                    setValue('coverPhotoUrl', '', { shouldDirty: true, shouldValidate: true });
                                }
                            }}
                            label={t('CampaignsPage.addCoverPhoto')}
                            disabled={isView}
                        />
                    )}
                />
                {coverPhotoEnabled ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(0, 0, 0, 0.6)', margin: '4px 0 8px', display: 'block' }}
                        >
                            {t('FeedbackSurveys.recommendedDimension')}
                        </Typography>
                        <ImageUploaderSingle
                            maxSize={10 * MB}
                            logoUrl={coverPhotoUrl}
                            disabled={isUploading || isView}
                            onDelete={onDeleteLogo}
                            uploadLogo={uploadLogo}
                            style={{ marginBottom: 16 }}
                            isUploading={isUploading}
                        />
                    </>
                ) : null}
            </div>
            <div>
                <Controller
                    control={control}
                    name="pinEnabled"
                    render={({ field, ref }) => (
                        <Switch
                            {...field}
                            ref={ref}
                            checked={getValues('pinEnabled')}
                            handleChange={event => {
                                setValue('pinEnabled', event.target.checked, {
                                    shouldDirty: true,
                                    shouldValidate: true,
                                });
                                if (!event.target.checked) {
                                    setValue('pin', '', { shouldDirty: true, shouldValidate: true });
                                    clearErrors('pin');
                                }
                            }}
                            label={t('CouponsPage.activationPinCode')}
                            disabled={isView}
                        />
                    )}
                />
                {pinEnabled ? (
                    <>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginBottom: '8px' }}
                        >
                            {t('CouponsPage.requireStaffForPin')}
                        </Typography>
                        <Controller
                            control={control}
                            name="pin"
                            render={({ field, fieldState: { error } }) => (
                                <OtpCode
                                    length={4}
                                    {...field}
                                    error={!!error}
                                    helperText={error ? t(error.message, { count: 4 }) : null}
                                    disabled={isView}
                                    style={{ width: 'calc(50% - 4px)' }}
                                />
                            )}
                        />
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default Settings;
